import { useEffect, useMemo, useState } from "react";
import { useFilter } from "../../../../hooks/useFilter";
import {
	flattenSelection,
	selectionToNumericIds,
	selectionToSortedIdsKey,
	useFilterContextV2,
} from "../../../../contexts/FilterContextV2";
import "../styles.css";
import axios from "../../../../services/axios";

export function InfoCattleMap() {
	const { balanceAnimal, balance } = useFilter();
	const { selectedFarms, selectedAreas } = useFilterContextV2();

	const farmsKey = useMemo(
		() => selectionToSortedIdsKey(selectedFarms),
		[selectedFarms],
	);
	const areasKey = useMemo(
		() => selectionToSortedIdsKey(selectedAreas),
		[selectedAreas],
	);

	const [areaAcreage, setAreaAcreage] = useState(0);
	const [capacityRate, setCapacityRate] = useState<number | null>(null);
	const [adjustments, setAdjustments] = useState<number | null>(null);

	useEffect(() => {
		const farmsIds = selectionToNumericIds(selectedFarms);
		const farmSet = new Set(farmsIds.map(String));
		const areaOpts = flattenSelection(selectedAreas);
		const areasIds = areaOpts
			.filter((area) => farmSet.has(String(area.info?.farm?.id)))
			.map((area) => Number(area.value));

		if (areaOpts.length !== 0) {
			setAreaAcreage(
				areaOpts
					.filter((area) => areasIds.includes(Number(area.value)))
					.reduce((acc, item) => acc + (item.info?.acreage ?? 0), 0),
			);
		} else {
			setAreaAcreage(0);
		}
	}, [farmsKey, areasKey]);

	useEffect(() => {
		if (!balanceAnimal?.length || areaAcreage === 0) {
			setCapacityRate(null);
			return;
		}
		const sumTotalUa = balanceAnimal.reduce(
			(acc, item) => acc + Math.abs(item.total_uas),
			0,
		);
		setCapacityRate(Number((sumTotalUa / areaAcreage).toFixed(2)));
	}, [balanceAnimal, areaAcreage]);

	useEffect(() => {
		const farmsIds = selectionToNumericIds(selectedFarms);
		const farmSet = new Set(farmsIds.map(String));
		const areaOpts = flattenSelection(selectedAreas);
		const areasIds = areaOpts
			.filter((area) => farmSet.has(String(area.info?.farm?.id)))
			.map((area) => Number(area.value));

		const controller = new AbortController();

		if (farmsIds.length !== 0 && areasIds.length !== 0) {
			axios
				.get(`/adjustments?farmId=${farmsIds}&areaIds=${areasIds}`, {
					signal: controller.signal,
				})
				.then(({ data }) => {
					setAdjustments(
						data.length > 0
							? data.reduce(
									(sum: number, adjustment: { balance: number }) =>
										sum + adjustment.balance,
									0,
								)
							: 0,
					);
				})
				.catch((err: { code?: string }) => {
					if (err?.code === "ERR_CANCELED") return;
					console.error(err);
				});
		} else {
			setAdjustments(null);
		}

		return () => {
			controller.abort();
		};
	}, [farmsKey, areasKey]);

	// Calcular balanceTotal a partir do balance do context
	const balanceTotal = balance?.reduce((acc, item) => acc + item.end_balance, 0) || 0;

	return (
		<section className="dashBoardInfo__container" style={{ marginTop: 30 }}>
			<div className="dashBoardInfoSplited__owner--container">
				<small>Ajuste atual</small>
				<h6
					className={
						adjustments && adjustments <= 0
							? "dashBoardAdjustment__negative"
							: "dashBoardAdjustment__positive"
					}
				>
					{adjustments || 0}
				</h6>
			</div>

			<div className="dashBoardInfo__dark--container">
				<small>Saldo previsto</small>
				<h6>{balanceTotal - (adjustments || 0)}</h6>
			</div>

			<div className="dashBoardInfoGreen__owner--container">
				<small>lotação (ua/ha)</small>
				<h6>{capacityRate || 0}</h6>
			</div>
		</section>
	);
}
